import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Bot, Users, Settings, MessageSquare, Music, Shield, Construction, ExternalLink } from 'lucide-react';

export default function CommunityDashboard() {
  const { language } = useLanguage();

  const sections = [
    { id: 'members', nameAr: 'الأعضاء', nameEn: 'Members', icon: Users, code: 'COM-001' },
    { id: 'bot-settings', nameAr: 'إعدادات البوت', nameEn: 'Bot Settings', icon: Settings, code: 'COM-002' },
    { id: 'chat', nameAr: 'المحادثات', nameEn: 'Chat Logs', icon: MessageSquare, code: 'COM-003' },
    { id: 'music', nameAr: 'الموسيقى', nameEn: 'Music Control', icon: Music, code: 'COM-004' },
    { id: 'moderation', nameAr: 'الإشراف', nameEn: 'Moderation', icon: Shield, code: 'COM-005' },
  ];

  return (
    <DashboardLayout
      title="606 Community"
      titleEn="606 Community"
      subtitle="إدارة بوت ديسكورد"
      subtitleEn="Discord Bot Management"
      screenCode="COM-000"
      breadcrumb={[
        { labelAr: '606 Community', labelEn: '606 Community' }
      ]}
    >
      <section className="py-8">
        {/* Bot Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-[#5865F2]/20 border border-[#5865F2]/30 flex items-center justify-center">
                <Bot className="h-8 w-8 text-[#5865F2]" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">606 Community Bot</h3>
                <div className="flex items-center gap-2 mt-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-sm">
                    {language === 'ar' ? 'متصل' : 'Online'}
                  </span>
                </div>
              </div>
            </div>
            <a
              href="https://gaming-hub-bot.preview.emergentagent.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-[#5865F2] hover:bg-[#4752c4] rounded-xl text-white font-medium transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              {language === 'ar' ? 'فتح لوحة التحكم' : 'Open Dashboard'}
            </a>
          </div>
        </motion.div>

        {/* Sections Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group relative bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-center cursor-pointer transition-all duration-300 hover:border-[#5865F2]/50 hover:bg-white/[0.04] hover:shadow-[0_8px_32px_rgba(88,101,242,0.12)] overflow-hidden"
            >
              {/* Screen Code Badge */}
              <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#5865F2]/10 rounded-md">
                <span className="text-[10px] font-mono text-[#5865F2]">{section.code}</span>
              </div>
              
              <div className="relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#5865F2]/10 border border-[#5865F2]/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-[#5865F2]/20 transition-all duration-300">
                  <section.icon className="h-8 w-8 text-[#5865F2]" strokeWidth={1.5} />
                </div>
                <h3 className="text-[#F5F5F5] font-bold text-sm group-hover:text-[#5865F2] transition-colors">
                  {language === 'ar' ? section.nameAr : section.nameEn}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Info Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-[#5865F2]/5 rounded-2xl border border-[#5865F2]/20">
            <Construction className="h-6 w-6 text-[#5865F2]" />
            <span className="text-[#A1A1AA]">
              {language === 'ar' 
                ? 'يمكنك إدارة البوت من لوحة التحكم الخارجية'
                : 'You can manage the bot from the external dashboard'}
            </span>
          </div>
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
